import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const [settings, setSettings] = useState({
    veicoli: [],
    personale: [],
    turni_options: [],
    extratime_options: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { logout, getAuthHeader, user } = useAuth();
  const navigate = useNavigate();

  // New item inputs
  const [newVeicolo, setNewVeicolo] = useState('');
  const [newPersonale, setNewPersonale] = useState('');
  const [newTurno, setNewTurno] = useState('');
  const [newExtratime, setNewExtratime] = useState('');

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, {
        headers: getAuthHeader()
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Errore caricamento impostazioni:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, {
        headers: getAuthHeader()
      });
      alert('Impostazioni salvate con successo!');
    } catch (error) {
      console.error('Errore salvataggio impostazioni:', error);
      alert(error.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // Veicoli
  const addVeicolo = () => {
    if (newVeicolo.trim()) {
      setSettings({...settings, veicoli: [...settings.veicoli, newVeicolo.trim()]});
      setNewVeicolo('');
    }
  };

  const removeVeicolo = (index) => {
    setSettings({...settings, veicoli: settings.veicoli.filter((_, i) => i !== index)});
  };

  // Personale
  const addPersonale = () => {
    if (newPersonale.trim()) {
      setSettings({...settings, personale: [...settings.personale, newPersonale.trim()]});
      setNewPersonale('');
    }
  };

  const removePersonale = (index) => {
    setSettings({...settings, personale: settings.personale.filter((_, i) => i !== index)});
  };

  // Turni
  const addTurno = () => {
    const value = parseFloat(newTurno);
    if (!isNaN(value) && value > 0) {
      const sorted = [...settings.turni_options, value].sort((a, b) => a - b);
      setSettings({...settings, turni_options: sorted});
      setNewTurno('');
    }
  };

  const removeTurno = (index) => {
    setSettings({...settings, turni_options: settings.turni_options.filter((_, i) => i !== index)});
  };

  // Extratime
  const addExtratime = () => {
    const value = parseFloat(newExtratime);
    if (!isNaN(value) && value >= 0) {
      const sorted = [...settings.extratime_options, value].sort((a, b) => a - b);
      setSettings({...settings, extratime_options: sorted});
      setNewExtratime('');
    }
  };

  const removeExtratime = (index) => {
    setSettings({...settings, extratime_options: settings.extratime_options.filter((_, i) => i !== index)});
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="settings-page">
      {/* Navbar */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center space-x-3">
              <h1 className="text-base font-semibold text-gray-900">BETTI SRL</h1>
              <span className="text-xs text-gray-400">|</span>
              <span className="text-xs text-gray-500">Impostazioni</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-xs text-gray-600">{user?.username}</span>
              <button
                onClick={() => navigate('/admin')}
                className="text-xs text-gray-500 hover:text-gray-900"
              >
                Amministrazione
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-xs text-gray-500 hover:text-gray-900"
              >
                Dashboard
              </button>
              <button
                onClick={logout}
                className="text-xs text-gray-500 hover:text-gray-900"
              >
                Esci
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Impostazioni Applicazione</h2>
            <p className="text-sm text-gray-600 mt-1">Configura veicoli, personale e opzioni turni</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-gray-900 text-white text-sm hover:bg-gray-800 disabled:opacity-50"
            data-testid="save-settings-btn"
          >
            {saving ? 'Salvataggio...' : 'Salva Modifiche'}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Veicoli */}
            <div className="bg-white border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Veicoli</h3>
              <div className="space-y-3">
                {settings.veicoli.map((veicolo, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200">
                    <span className="text-sm text-gray-900">{veicolo}</span>
                    <button
                      onClick={() => removeVeicolo(index)}
                      className="text-red-600 hover:text-red-800 text-sm"
                      data-testid={`remove-veicolo-${index}`}
                    >
                      Rimuovi
                    </button>
                  </div>
                ))}
                <div className="flex gap-2 mt-4">
                  <input
                    type="text"
                    value={newVeicolo}
                    onChange={(e) => setNewVeicolo(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addVeicolo()}
                    placeholder="Nome veicolo (es: FIAT DUCATO BIANCO AB123CD)"
                    className="flex-1 px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:border-gray-900"
                    data-testid="new-veicolo-input"
                  />
                  <button
                    onClick={addVeicolo}
                    className="px-4 py-2 bg-gray-900 text-white text-sm hover:bg-gray-800"
                    data-testid="add-veicolo-btn"
                  >
                    Aggiungi
                  </button>
                </div>
              </div>
            </div>

            {/* Personale */}
            <div className="bg-white border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Personale</h3>
              <div className="space-y-3">
                {settings.personale.map((persona, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200">
                    <span className="text-sm text-gray-900">{persona}</span>
                    <button
                      onClick={() => removePersonale(index)}
                      className="text-red-600 hover:text-red-800 text-sm"
                      data-testid={`remove-personale-${index}`}
                    >
                      Rimuovi
                    </button>
                  </div>
                ))}
                <div className="flex gap-2 mt-4">
                  <input
                    type="text"
                    value={newPersonale}
                    onChange={(e) => setNewPersonale(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addPersonale()}
                    placeholder="Nome e cognome (es: Mario Rossi)"
                    className="flex-1 px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:border-gray-900"
                    data-testid="new-personale-input"
                  />
                  <button
                    onClick={addPersonale}
                    className="px-4 py-2 bg-gray-900 text-white text-sm hover:bg-gray-800"
                    data-testid="add-personale-btn"
                  >
                    Aggiungi
                  </button>
                </div>
              </div>
            </div>

            {/* Opzioni Turni */}
            <div className="bg-white border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Opzioni Turni</h3>
              <p className="text-sm text-gray-600 mb-3">Valori disponibili per i turni (es: 0.5, 1, 1.5, 2...)</p>
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {settings.turni_options.map((turno, index) => (
                    <div key={index} className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200">
                      <span className="text-sm text-gray-900">{turno}</span>
                      <button
                        onClick={() => removeTurno(index)}
                        className="text-red-600 hover:text-red-800 text-xs"
                        data-testid={`remove-turno-${index}`}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-4">
                  <input
                    type="number"
                    step="0.5"
                    value={newTurno}
                    onChange={(e) => setNewTurno(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTurno()}
                    placeholder="Valore (es: 4.5)"
                    className="w-32 px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:border-gray-900"
                    data-testid="new-turno-input"
                  />
                  <button
                    onClick={addTurno}
                    className="px-4 py-2 bg-gray-900 text-white text-sm hover:bg-gray-800"
                    data-testid="add-turno-btn"
                  >
                    Aggiungi
                  </button>
                </div>
              </div>
            </div>

            {/* Opzioni Extratime */}
            <div className="bg-white border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Opzioni Extratime</h3>
              <p className="text-sm text-gray-600 mb-3">Valori disponibili per l'extratime (es: 0, 0.5, 1, 2...)</p>
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {settings.extratime_options.map((extra, index) => (
                    <div key={index} className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200">
                      <span className="text-sm text-gray-900">{extra}</span>
                      <button
                        onClick={() => removeExtratime(index)}
                        className="text-red-600 hover:text-red-800 text-xs"
                        data-testid={`remove-extratime-${index}`}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-4">
                  <input
                    type="number"
                    step="0.5"
                    value={newExtratime}
                    onChange={(e) => setNewExtratime(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addExtratime()}
                    placeholder="Valore (es: 3)"
                    className="w-32 px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:border-gray-900"
                    data-testid="new-extratime-input"
                  />
                  <button
                    onClick={addExtratime}
                    className="px-4 py-2 bg-gray-900 text-white text-sm hover:bg-gray-800"
                    data-testid="add-extratime-btn"
                  >
                    Aggiungi
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer info */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200">
          <p className="text-sm text-blue-900">
            <strong>Nota:</strong> Le modifiche alle impostazioni si applicheranno immediatamente a tutti i nuovi report. I report esistenti non saranno modificati.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Settings;
